import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { History, ChevronDown, User } from "lucide-react";
import { Modal } from "./ui/Modal";
import { LoadingSpinner } from "./ui/LoadingSpinner";
import { auditApi } from "../lib/auditApi";
import type { AuditRecord } from "../lib/auditApi";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  entityType: string;
  entityId: string;
  /** Display name shown in the modal title, e.g. "John Doe" */
  label?: string;
}

function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    "add-member":    "Added to business",
    "update-member": "Member updated",
    "remove-member": "Removed from business",
  };
  return labels[action] ?? action.replace(/-/g, " ");
}

function ActionBadge({ action }: { action: string }) {
  const danger = action.includes("remove") || action.includes("delete") || action.includes("reject");
  const success = action.includes("add") || action.includes("create") || action.includes("approve") || action.includes("release");
  const cls = danger
    ? "bg-[#fef2f2] text-[#dc2626]"
    : success
    ? "bg-[#f0fdf4] text-[#16a34a]"
    : "bg-[#f1f5f9] text-[#475569]";
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${cls}`}>
      {actionLabel(action)}
    </span>
  );
}

function ChangesTable({ changes }: { changes: AuditRecord["changes"] }) {
  if (!changes || Object.keys(changes).length === 0) return null;
  return (
    <table className="mt-2 w-full text-xs border border-[#e2e8f0] rounded-lg overflow-hidden">
      <thead className="bg-[#f8fafc]">
        <tr>
          <th className="px-2 py-1.5 text-left font-semibold text-[#94a3b8] uppercase tracking-wide text-[10px]">Field</th>
          <th className="px-2 py-1.5 text-left font-semibold text-[#94a3b8] uppercase tracking-wide text-[10px]">Before</th>
          <th className="px-2 py-1.5 text-left font-semibold text-[#94a3b8] uppercase tracking-wide text-[10px]">After</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#f1f5f9]">
        {Object.entries(changes).map(([field, { before, after }]) => (
          <tr key={field}>
            <td className="px-2 py-1.5 font-medium text-[#475569]">{field}</td>
            <td className="px-2 py-1.5 text-[#94a3b8] line-through">{String(before ?? "—")}</td>
            <td className="px-2 py-1.5 text-[#0f172a]">{String(after ?? "—")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AuditHistoryModal({ isOpen, onClose, businessId, entityType, entityId, label }: Props) {
  const [cursor, setCursor] = useState<string | undefined>();
  const [allItems, setAllItems] = useState<AuditRecord[]>([]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["audit-history", businessId, entityType, entityId, cursor],
    queryFn: async () => {
      const page = await auditApi.getHistory(businessId, entityType, entityId, cursor);
      setAllItems((prev: AuditRecord[]) => (cursor ? [...prev, ...page.items] : page.items));
      return page;
    },
    enabled: isOpen && !!businessId && !!entityType && !!entityId,
    staleTime: 0,
  });

  const handleLoadMore = () => {
    if (data?.nextKey) setCursor(data.nextKey);
  };

  const handleClose = () => {
    setCursor(undefined);
    setAllItems([]);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={label ? `Change History — ${label}` : "Change History"}
      size="md"
    >
      {isLoading && allItems.length === 0 ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : allItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <History className="h-8 w-8 text-[#cbd5e1] mb-2" />
          <p className="text-sm font-medium text-[#64748b]">No history yet</p>
          <p className="text-xs text-[#94a3b8] mt-0.5">Changes will appear here once recorded.</p>
        </div>
      ) : (
        <div className="space-y-0">
          <ol className="relative border-l border-[#e2e8f0] ml-3 space-y-4">
            {allItems.map((item: AuditRecord) => (
              <li key={item.auditId} className="ml-4">
                {/* Timeline dot */}
                <div className="absolute -left-1.5 h-3 w-3 rounded-full bg-[#2447d9] border-2 border-white" />
                <div className="rounded-lg border border-[#f1f5f9] bg-[#f8fafc] p-3">
                  <div className="flex flex-wrap items-center justify-between gap-1 mb-1.5">
                    <ActionBadge action={item.action} />
                    <span className="text-[11px] text-[#94a3b8]">
                      {new Date(item.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                    <User className="h-3 w-3 text-[#94a3b8]" />
                    <span>{item.actorName || item.actorId}</span>
                    <span className="text-[#cbd5e1]">·</span>
                    <span className="text-[#94a3b8]">{item.page}</span>
                  </div>
                  <ChangesTable changes={item.changes} />
                  {(!item.changes || Object.keys(item.changes).length === 0) && (
                    <p className="mt-1.5 text-[11px] text-[#94a3b8] italic">No field changes recorded.</p>
                  )}
                </div>
              </li>
            ))}
          </ol>

          {data?.nextKey && (
            <div className="flex justify-center pt-4">
              <button
                onClick={handleLoadMore}
                disabled={isFetching}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-4 py-2 text-xs font-medium text-[#475569] hover:bg-[#f1f5f9] transition-colors disabled:opacity-60"
              >
                {isFetching ? <LoadingSpinner size="sm" /> : <ChevronDown className="h-3.5 w-3.5" />}
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
